/** One contract split across two repos: @vibe/form (vibe-lambda libs/vibe-form) mirrors these. */

export type FormFieldType =
    | 'text'
    | 'email'
    | 'tel'
    | 'url'
    | 'number'
    | 'date'
    | 'textarea'
    | 'select'
    | 'radio'
    | 'checkbox'
    | 'switch'
    | 'file';

interface FormFieldBase {
    name: string;
    label: string;
}

/**
 * A field's `value` is what the visitor put in it: text for every type, and for a `file` field the
 * `File` objects they picked, which `submit` uploads before sending what storage returned.
 */
export type FormField =
    | (FormFieldBase & { type: Exclude<FormFieldType, 'file'>; value: string })
    | (FormFieldBase & { type: 'file'; value: File[] });

export type MarketingChannel = 'email' | 'sms';

/** What reaches the endpoint: every value is a string by the time it is sent. */
export interface WireField {
    name: string;
    label: string;
    type: FormFieldType;
    value: string;
    /** Set only on a marketing consent checkbox, which submit() reads from the page itself. */
    consentType?: MarketingChannel;
}

export interface SubmitInput {
    formId: string;
    fields: FormField[];
    /** The <form> being sent, whose consent checkboxes go with it; found by `formId` when left out. */
    form?: HTMLFormElement;
}

/** Mirrors UploadGrantRDT. Every entry of `fields` is posted to S3 verbatim, the file last. */
export interface UploadGrant {
    uploadUrl: string;
    fields: Record<string, string>;
    key: string;
    url: string;
    maxSize: number;
    expiresAt: number;
}

export type FormErrorCode =
    | 'BAD_REQUEST'
    | 'FORM_NOT_FOUND'
    | 'CAPTCHA_FAILED'
    | 'FILE_REJECTED'
    | 'PREVIEW_UNAVAILABLE'
    | 'RATE_LIMITED'
    | 'NETWORK'
    | 'UNKNOWN';

export class RuntimeFormError extends Error {
    constructor(
        readonly status: number,
        readonly code: FormErrorCode,
        message: string,
    ) {
        super(message);
        this.name = 'RuntimeFormError';
    }
}

/** Keyed by capability rather than by the service that serves it, so a capability can move. */
export interface VibeGlobals {
    /** Written by the page shell. The site key must pair with the secret the server verifies against. */
    config?: {
        captchaSiteKey?: string;
        /** Present only on a designtime render; authorizes preview form submissions and services calls. */
        previewToken?: string;
    };
    form?: { submit(input: SubmitInput): Promise<void> };
}

/** Shared with the already-published auth and collections SDKs, which is why the alias lives here. */
export interface VibeRuntimeParameters {
    SiteAlias?: string;
    /** False on an editor or preview render. Absent counts as preview, never as runtime. */
    isRuntimeServer?: boolean;
}

export interface ReCaptchaRenderParameters {
    sitekey: string;
    size: 'invisible';
    callback: (token: string) => void;
    'error-callback'?: () => void;
}

export interface GoogleReCaptcha {
    render(
        container: HTMLElement,
        parameters: ReCaptchaRenderParameters,
    ): number;
    execute(widgetId: number): void;
    reset(widgetId: number): void;
    ready(callback: () => void): void;
}

declare global {
    interface Window {
        Parameters?: VibeRuntimeParameters;
        vibe?: VibeGlobals;
        grecaptcha?: GoogleReCaptcha;
    }
}
